import React from 'react';
import { Layout } from 'antd';
import { useLocation } from 'react-router-dom';
import AppHeader from './AppHeader';
import AppSider from './AppSider';
import MobileBottomNav from './MobileBottomNav';
import { useResponsive } from '@/hooks/useResponsive';
import AIChatBot from '@/components/common/AIChatBot';

const { Content } = Layout;

interface ResponsiveLayoutProps {
  children: React.ReactNode;
}

/**
 * 响应式布局组件
 * 
 * 根据屏幕尺寸自动切换布局:
 * - PC (>=1024px): 固定侧边栏 + 主内容区
 * - Tablet (768-1023px): 可折叠侧边栏 + 主内容区
 * - Mobile (<768px): 简化头部 + 全宽内容 + 底部导航
 * 
 * 家长端页面 (/parent/*) 使用极简布局，不显示导航元素
 */
const ResponsiveLayout: React.FC<ResponsiveLayoutProps> = ({ children }) => {
  const { isMobile } = useResponsive();
  const location = useLocation();
  
  // 检测是否是家长端页面
  const isParentRoute = location.pathname.startsWith('/parent');

  // 家长端使用极简布局（无侧边栏、无底部导航、无头部）
  if (isParentRoute) {
    return (
      <>
        <Layout style={{ minHeight: '100vh', background: '#fafafa' }}>
          <Content style={{ padding: 0 }}>
            {children}
          </Content>
        </Layout>
        <AIChatBot />
      </>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 顶部导航栏 - 放在最外层，确保全宽 */}
      <AppHeader />

      <Layout>
        {/* PC/Tablet 显示侧边栏 */}
        {!isMobile && <AppSider />}

        {/* 主内容区 */}
        <Content
          style={{
            margin: isMobile ? '12px' : '24px',
            padding: isMobile ? '16px' : '24px',
            background: '#fff',
            borderRadius: '8px',
            minHeight: 280,
            // 移动端底部留出空间给底部导航
            paddingBottom: isMobile ? 'calc(72px + env(safe-area-inset-bottom, 0px))' : undefined,
          }}
        >
          {children}
        </Content>
      </Layout>

      {/* 移动端显示底部导航 */}
      <MobileBottomNav />
      <AIChatBot />
    </Layout>
  );
};

export default ResponsiveLayout;
